const API_URL = process.env.REACT_APP_API_URL;

/**
 * Extract the best error message from a ProblemDetail response body, matching the
 * convention used across the other services (flowService et al.).
 */
function extractErrorMessage(errorBody, fallback) {
  if (!errorBody) return fallback;
  const detail = errorBody.detail;
  const title = errorBody.title;
  if (detail && title && title !== fallback) {
    return `${title}: ${detail}`;
  }
  return detail || fallback;
}

async function asJsonOrThrow(response, fallback) {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, fallback));
  }
  return await response.json();
}

/**
 * Client for the booking runtime's owner-facing API (the C1 ↔ C2 contract in
 * docs/launch/PLAN.md): the schedule a booking flow reads its availability from, the endpoint
 * key WhatsApp encrypts data-exchange requests with, and the bookings that result.
 */
class BookingService {
  /** The schedule. Always answers, an environment with nothing saved gets a weekday default. */
  async getSchedule(envId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/booking/schedule`
    );
    return asJsonOrThrow(response, 'Failed to load the booking schedule');
  }

  async saveSchedule(envId, schedule, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/booking/schedule`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(schedule),
      }
    );
    return asJsonOrThrow(response, 'Failed to save the booking schedule');
  }

  /** Bookings between two ISO dates, for the owner's list. */
  async listBookings(envId, { from, to }, authenticatedFetch) {
    const params = new URLSearchParams({ from, to });
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/booking/bookings?${params.toString()}`
    );
    return asJsonOrThrow(response, 'Failed to load bookings');
  }

  async cancelBooking(envId, bookingId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/booking/bookings/${bookingId}/cancel`,
      { method: 'POST' }
    );
    return asJsonOrThrow(response, 'Failed to cancel the booking');
  }

  /**
   * Whether this number can serve encrypted booking requests, and what WhatsApp says about the
   * key it holds. `configured: false` means the server has no encryption key at all, an honest
   * "not available here", not a problem the owner can fix.
   */
  async keyStatus(envId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/booking/keys/status`
    );
    return asJsonOrThrow(response, 'Failed to read the booking key status');
  }

  /** Generate a fresh key pair and publish the public half to WhatsApp. */
  async rotateKey(envId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/booking/keys/rotate`,
      { method: 'POST' }
    );
    return asJsonOrThrow(response, 'Failed to set up live booking');
  }

  /**
   * Which diary each booking flow books into.
   *
   * <p>A binding with no `flowId` is the environment default, used by every booking flow that has
   * no binding of its own; no binding at all means FlowGate's own schedule. `keySet` is all the
   * API will say about a credential, it is never returned.
   */
  async listProviders(envId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/booking/providers`
    );
    return asJsonOrThrow(response, 'Failed to read the booking providers');
  }

  /**
   * Bind a flow, or the whole environment when `flowId` is omitted.
   *
   * Omit `apiKey` to keep the stored one: the API never returned it, so there is nothing to send
   * back when only the event types are being changed.
   */
  async saveProvider(envId, binding, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/booking/providers`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(binding),
      }
    );
    return asJsonOrThrow(response, 'Failed to save where this flow books');
  }

  async deleteProvider(envId, bindingId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/booking/providers/${bindingId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      throw new Error('Failed to remove that booking provider');
    }
  }

  /**
   * The event types a Cal.com key can see, so the owner picks from their own calendar.
   *
   * POST because the key travels in the body, a key in a query string ends up in the access log.
   * Pass no key to use the one already stored for this binding.
   */
  async calComEventTypes(envId, { flowId, apiKey } = {}, authenticatedFetch) {
    const query = flowId ? `?flowId=${encodeURIComponent(flowId)}` : '';
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/booking/providers/calcom/event-types${query}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiKey ? { apiKey } : {}),
      }
    );
    return asJsonOrThrow(response, 'Could not read your Cal.com calendars');
  }
}

const bookingService = new BookingService();
export default bookingService;
